import React, { useEffect, useState } from "react";
import { getCurrentFolderMessages, getMessageStats } from "../../../services/restAPI/RestApi";
import { Button, Spin, Tabs } from "../../BP2Components";
import {
   addIndexesToItems,
   findAndCompareObject,
   findObjectWithMaxIndex,
} from "./InfiniteListScripts";
import Widget from "../../Widget/Widget";
import SettingsModal from "../../Modal/SettingsModal";
import InfiniteListModal from "../WidgetsSettings/InfiniteScroll/InfiniteListModal";
import { useLocation } from "react-router-dom";
import { getLabelFromTechName } from "../../Newsletter/NewsletterSteps/ThirdStep";
import TabPane from "../../BP2Components/Tabs/TabPane";
import IntlMessages from "../../../util/IntlMessages";
import InfiniteListContent from "./InfiniteListContent";
import "./InfiniteList.less";

interface InfiniteListProps {
   value: any;
   role: any;
}

const InfiniteList: React.FC<InfiniteListProps> = ({ value, role }) => {
   const [casesList, setCasesList] = useState<any>();
   const [readCases, setReadCases] = useState<any[]>();
   const [isLoading, setIsLoading] = useState(true);
   const [reload, setReload] = useState(false);
   const [filteredList, setFilteredList] = useState<any>();
   const [pageToDownload, setPageToDownload] = useState<number>(1);
   const [listResponse, setListResponse] = useState<any>();
   const [statsResponse, setStatsResponse] = useState<any>();
   const [isSearchingActive, setIsSearchingActive] = useState<boolean>(false);
   const [currentProfile, setCurrentProfile] = useState<number | null>(null);
   const location = useLocation();

   // here we define after which object from the end we start downloading a new page
   const startNewPageDownloadBy = 3;
   const isProfilesAdded = value?.profileList?.length > 0;

   useEffect(() => {
      const getStats = async () => {
         try {
            const resp = await getMessageStats(value.listId, currentProfile ?? "default");
            setStatsResponse(resp.data.stats);
         } catch (error) {
            console.error("Error fetching stats:", error);
         }
      };

      getStats();
   }, [value.listId, currentProfile]);

   useEffect(() => {
      getCases(pageToDownload);
   }, [pageToDownload, currentProfile]);

   useEffect(() => {
      setPageToDownload(1);
      setCasesList([]);
      setReadCases([]);
      setIsLoading(true);
      handleGetCases();
   }, [value.listId]);

   useEffect(() => {
      const params = new URLSearchParams(location.search);
      const queryParam = params.get("query");
      if (queryParam && listResponse) {
         setIsSearchingActive(true);
         const subject = getLabelFromTechName("temat", listResponse?.data?.default?.columns);
         const sender = getLabelFromTechName("nadawca", listResponse?.data?.default?.columns);
         const tags = getLabelFromTechName("tagi", listResponse?.data?.default?.columns);
         const privateTags = getLabelFromTechName(
            "tagi_priv",
            listResponse?.data?.default?.columns,
         );
         const lowercaseInsertedText = queryParam.toLowerCase();
         const matchedCases = casesList.filter(
            (item) =>
               item?.[subject]?.toLowerCase()?.includes(lowercaseInsertedText) ||
               item?.[sender]?.value?.toLowerCase()?.includes(lowercaseInsertedText) ||
               item?.[tags]?.toLowerCase()?.includes(lowercaseInsertedText) ||
               item?.[privateTags]?.toLowerCase()?.includes(lowercaseInsertedText),
         );
         setFilteredList(matchedCases);
      } else {
         setIsSearchingActive(false);
      }
      if (!queryParam && listResponse) {
         setCasesList([]);
         getCases(1);
      }
   }, [location.search]);

   const getCases = (downloadPage: number) => {
      setReload(true);
      try {
         getCurrentFolderMessages(value.listId, currentProfile, downloadPage, 10).then((res) => {
            setListResponse(res);
            setReload(false);
            if (pageToDownload === res?.data?.default?.meta?.totalPages) {
               setIsLoading(false);
            }
            setCasesList((prevCasesList) =>
               prevCasesList && prevCasesList.length > 0 && downloadPage !== 1
                  ? [
                       ...prevCasesList,
                       ...addIndexesToItems(
                          res?.data?.default?.data,
                          casesList && casesList?.length > 0
                             ? findObjectWithMaxIndex(casesList).index
                             : 0,
                       ),
                    ]
                  : addIndexesToItems(
                       res?.data?.default?.data,
                       casesList && casesList?.length > 0
                          ? findObjectWithMaxIndex(casesList).index
                          : 0,
                    ),
            );
         });
      } catch (err) {
         setReload(false);
      }
   };

   const handleGetCases = () => {
      getCases(1);
   };

   const updateCasesListByProfile = (profile) => {
      setCurrentProfile(profile);
      setPageToDownload(1);
   };

   const handleVisibilityChange = (isVisible, singleCase) => {
      if (isVisible) {
         if (
            findAndCompareObject(casesList, singleCase, startNewPageDownloadBy) &&
            !readCases?.includes(singleCase) &&
            !new URLSearchParams(location.search).get("query") &&
            pageToDownload < listResponse?.data?.default?.meta?.totalPages
         ) {
            setPageToDownload(pageToDownload + 1);
         }

         if (!readCases?.includes(singleCase)) {
            checkOrAddReadItem(singleCase);
         }
      }
   };

   const checkOrAddReadItem = (singleCase) => {
      setReadCases((prevCaseList) =>
         prevCaseList && prevCaseList.length > 0 ? [...prevCaseList, singleCase] : [singleCase],
      );
   };

   //here we handle hidding topBar dragging icon from infinite list widget
   const dragHandle = document.querySelector(".drag-handle");

   if (dragHandle instanceof HTMLElement && role === "MODERATOR") {
      dragHandle.style.display = "none";
   }

   return (
      <Widget
         title={value?.title}
         role={role}
         hideCardHead={role === "OWNER" ? false : true}
         extra={
            (role === "OWNER" || role === "MODERATOR") && (
               //@ts-ignore
               <SettingsModal
                  widgetName={value.name}
                  widgetId={value.id}
                  icon="setting"
                  deleteIcon="true"
                  children={<InfiniteListModal value={value} />}
               />
            )
         }
         bgColor="transparent"
         className="infinite-list-widget"
         children={
            isSearchingActive ? (
               filteredList?.length
            ) : casesList?.length > 0 ? (
               isProfilesAdded ? (
                  <Tabs defaultActiveKey="1">
                     <TabPane
                        tab={<IntlMessages id="folder.allReceived" />}
                        key={1}
                        updateCasesList={updateCasesListByProfile}
                        profileId={0}
                     >
                        <InfiniteListContent
                           value={value}
                           getCases={getCases}
                           setPageToDownload={setPageToDownload}
                           handleVisibilityChange={handleVisibilityChange}
                           casesList={isSearchingActive ? filteredList : casesList}
                           listResponse={listResponse}
                           isLoading={isLoading}
                           isProfilesAdded={isProfilesAdded}
                           actionCallback={handleGetCases}
                           stats={statsResponse}
                        />
                     </TabPane>
                     {value?.profileList?.map((item) => (
                        <TabPane
                           tab={item?.profileName}
                           key={item?.profileId}
                           updateCasesList={updateCasesListByProfile}
                           profileId={item?.profileId}
                        >
                           <InfiniteListContent
                              getCases={getCases}
                              setPageToDownload={setPageToDownload}
                              value={value}
                              handleVisibilityChange={handleVisibilityChange}
                              casesList={isSearchingActive ? filteredList : casesList}
                              listResponse={listResponse}
                              isLoading={isLoading}
                              isProfilesAdded={isProfilesAdded}
                              actionCallback={handleGetCases}
                              stats={statsResponse}
                           />
                        </TabPane>
                     ))}
                  </Tabs>
               ) : (
                  <InfiniteListContent
                     value={value}
                     getCases={getCases}
                     setPageToDownload={setPageToDownload}
                     handleVisibilityChange={handleVisibilityChange}
                     casesList={isSearchingActive ? filteredList : casesList}
                     listResponse={listResponse}
                     isLoading={isLoading}
                     isProfilesAdded={isProfilesAdded}
                     actionCallback={handleGetCases}
                     stats={statsResponse}
                  />
               )
            ) : (
               <div className="infinite-list-widget-empty">
                  {!reload ? (
                     <>
                        <span className="infinite-list-widget-empty__message">
                           <IntlMessages id="infinity.noCasesinCasesList" />
                        </span>
                        <Button
                           onClick={() => getCases(1)}
                           style={{ marginTop: "1rem" }}
                           buttonType="primary"
                        >
                           <IntlMessages id="global.reload" />
                        </Button>
                     </>
                  ) : (
                     <Spin />
                  )}
               </div>
            )
         }
      ></Widget>
   );
};

export default InfiniteList;
